import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Container,
  Paper,
  IconButton,
  Divider,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, Save, Edit, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { diagnosticCentersService } from './diagnosticCenters.service';
import { DiagnosticCenter } from './diagnosticCenters.dto';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '@/helpers';

const EditDiagnosticCenter = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<Partial<DiagnosticCenter>>({
    id: 0,
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    status: 1,
    createdAt: '',
    updatedAt: '',
  });

  useEffect(() => {
    if (id) {
      fetchDiagnosticCenter();
    }
  }, [id]);

  const fetchDiagnosticCenter = async () => {
    if (!token || !id) return;

    try {
      setFetchingData(true);
      diagnosticCentersService.setLogoutCallback(logout);
      const data = await diagnosticCentersService.getDiagnosticCenter(
        token,
        parseInt(id)
      );

      if (data) {
        setFormData(data);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch diagnostic center';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      setTimeout(() => {
        navigate('/diagnostic-centers');
      }, 2000);
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange =
    (field: keyof DiagnosticCenter) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | number = (event.target as HTMLInputElement).value;

      // Special handling for phone field - only allow + and numbers
      if (field === 'phone') {
        value = value.replace(/[^+0-9]/g, '');
        // Ensure + can only be at the start
        if (value.includes('+')) {
          const plusCount = (value.match(/\+/g) || []).length;
          if (plusCount > 1 || value.indexOf('+') !== 0) {
            value = value.replace(/\+/g, '');
            if ((event.target.value as string).startsWith('+')) {
              value = '+' + value;
            }
          }
        }
      }

      // Special handling for postal_code field - only allow numbers
      if (field === 'postal_code') {
        value = value.replace(/[^0-9]/g, '');
      }

      // Handle status field
      if (field === 'status') {
        value = Number(value);
      }

      setFormData({
        ...formData,
        [field]: value,
      });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors({ ...errors, [field]: '' });
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?\d+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number can only contain + and numbers';
    }
    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.postal_code?.trim()) {
      newErrors.postal_code = 'Postal code is required';
    } else if (!/^\d+$/.test(formData.postal_code)) {
      newErrors.postal_code = 'Postal code can only contain numbers';
    }
    if (!formData.country?.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix all validation errors',
        severity: 'error',
      });
      return;
    }

    if (!token || !id) return;

    try {
      setLoading(true);
      const updateData: Partial<DiagnosticCenter> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        status: formData.status,
      };

      await diagnosticCentersService.updateDiagnosticCenter(
        token,
        parseInt(id),
        updateData
      );

      setSnackbar({
        open: true,
        message: 'Diagnostic center updated successfully',
        severity: 'success',
      });
      setTimeout(() => {
        navigate('/diagnostic-centers');
      }, 1500);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update diagnostic center';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/diagnostic-centers');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const statuses = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return 'success';
      case 0:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  if (fetchingData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading diagnostic center data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/diagnostic-centers')}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Diagnostic Center
              </Typography>
            </Box>
            <Chip
              label={getStatusText(formData.status || 0)}
              color={getStatusColor(formData.status || 0)}
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update diagnostic center information and settings
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Settings Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Diagnostic Center Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {statuses.map(status => (
                      <MenuItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formData.createdAt && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Created on
                    </Typography>
                    <Typography variant="h6">
                      {formatDate(formData.createdAt)}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                        placeholder="e.g., City Hospital Diagnostic Center"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                        placeholder="contact@center.com"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        error={!!errors.phone}
                        helperText={errors.phone || 'Format: +542121516'}
                        placeholder="+542121516"
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Address Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={formData.address}
                        onChange={handleChange('address')}
                        error={!!errors.address}
                        helperText={errors.address}
                        required
                        multiline
                        rows={2}
                        placeholder="Street address, building number"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.city}
                        onChange={handleChange('city')}
                        error={!!errors.city}
                        helperText={errors.city}
                        required
                        placeholder="e.g., Buenos Aires"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={formData.postal_code}
                        onChange={handleChange('postal_code')}
                        error={!!errors.postal_code}
                        helperText={errors.postal_code}
                        required
                        placeholder="12345"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={formData.country}
                        onChange={handleChange('country')}
                        error={!!errors.country}
                        helperText={errors.country}
                        required
                        placeholder="e.g., Argentina"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={loading}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditDiagnosticCenter;
